import React from 'react';
import { Mark } from './ui/kit';
import { MARKS } from '../lib/marks';

interface ErrorFallbackProps {
  error: Error | null;
  resetError: () => void;
}

/**
 * The crash screen. It must not use anything from react-router: `ErrorBoundary`
 * wraps `<Router>` in App.tsx, so a `<Link>` here throws while rendering and the
 * user gets a blank page instead of a way out. A plain anchor is also the more
 * honest control — a full reload is what actually clears a crashed tree.
 *
 * `Mark` is safe to pull from the kit: importing the module that also exports
 * router-aware components is fine, only *rendering* one outside a Router throws.
 */

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 px-4">
      <div className="max-w-md w-full text-center">
        {/* A blown fuse — the drawing says "one part tripped", not "everything is lost". */}
        <Mark src={MARKS.broken} size="xl" className="mx-auto" />

        {/* Error Message */}
        <div className="mx-auto mt-6 mb-5 h-px w-10 bg-zinc-300" />
        <div className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 mb-2.5">
          Unexpected error
        </div>
        <h1 className="text-2xl font-display font-bold text-primary mb-3">
          Something went wrong
        </h1>
        <p className="text-muted mb-8 leading-relaxed">
          This screen stopped rendering. Nothing was lost — your data is on the server,
          not in this tab. Try again, or reload the dashboard.
        </p>

        {/* Error Details (Development Only) */}
        {import.meta.env.DEV && error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-left">
            <p className="text-sm font-medium text-red-800 mb-2">
              Error Details (Dev Only):
            </p>
            <p className="text-xs font-mono text-red-700 break-all">
              {error.message}
            </p>
            {error.stack && (
              <details className="mt-2">
                <summary className="text-xs text-red-600 cursor-pointer hover:underline">
                  Stack trace
                </summary>
                <pre className="mt-2 text-xs font-mono text-red-600 overflow-auto max-h-32 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={resetError}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-ink-soft text-white font-medium rounded-full transition-all duration-200 hover:-translate-y-0.5"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            Try Again
          </button>

          <a
            href="/dashboard"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-200 hover:bg-zinc-300 text-primary font-medium rounded-xl transition-all duration-200"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
              />
            </svg>
            Go to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
